// =============================================================================
// SimSense — Coaching Modal (OT-273)
// =============================================================================
// Fullscreen modal overlay that displays the Advanced Coaching Report inline on the
// session page. Fetches and renders the same coaching data as the standalone
// Coaching page, but without navigating away.
// =============================================================================

import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { api } from "../utils/api";
import { formatLapTime } from "../utils/formatters";
import "../pages/Coaching.css";

interface CoachingSection {
  title: string;
  icon: string;
  content: string;
}

interface CoachingReport {
  tier: "free" | "pro";
  summary?: string;
  score?: number;
  pace_delta_seconds?: number;
  technique_grades?: Record<string, string>;
  priority_checklist?: string[];
  corner_stoplights?: Record<string, Record<string, string>>;
  car_balance_status?: string;
  observations?: string[];
  myTrend?: any[];
  referenceLaps?: any[];
  sections?: CoachingSection[];
  content?: string;
}

interface CoachingModalProps {
  sessionId: string;
  stintIndex: number;
  session: any;
  onClose: () => void;
}

// --- Helpers ---

const ACTION_TITLES = ["action plan", "development plan"];
function classifySection(title: string): "priority" | "action" | "detail" {
  const lower = title.toLowerCase();
  if (ACTION_TITLES.some((t) => lower.includes(t))) return "action";
  return "detail";
}

function getSeverityColor(text: string): string {
  const lower = (text || "").toLowerCase();
  if (lower.includes("critical") || lower.includes("significant") || lower.includes("major") || lower.includes("losing") || lower.includes("red") || lower.includes("oversteer") || lower.includes("bad"))
    return "var(--bad)";
  if (lower.includes("inconsistent") || lower.includes("could") || lower.includes("slight") || lower.includes("moderate") || lower.includes("yellow") || lower.includes("understeer") || lower.includes("warn"))
    return "var(--warn)";
  return "var(--good)";
}

function getGradeColor(grade: string): "good" | "warn" | "bad" {
  const letter = (grade || "").toUpperCase().charAt(0);
  if (letter === "A") return "good";
  if (letter === "B") return "warn";
  return "bad";
}

interface CoachingUsage {
  full_reports_used: number;
  full_reports_remaining: number | null;
  full_report_cap: number | null;
}

export function CoachingModal({ sessionId, stintIndex, session, onClose }: CoachingModalProps) {
  const [report, setReport] = useState<CoachingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<CoachingUsage | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  const toggleSection = (idx: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Fetch coaching data + usage
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const cData = await api<any>(`/api/coaching/report/${sessionId}/${stintIndex}?depth=pro`);
        // Fetch trend + best laps + usage in parallel
        const [trend, laps, usageData] = await Promise.all([
          api<any[]>(`/api/insights/my-trend/${session.trackId}/${encodeURIComponent(session.carName)}`).catch(() => []),
          api<any[]>(`/api/leaderboard/best-laps/${encodeURIComponent(session.trackName)}/${encodeURIComponent(session.carName)}`).catch(() => []),
          api<CoachingUsage>(`/api/coaching/usage/me`).catch(() => null),
        ]);
        setReport({ ...cData, myTrend: trend, referenceLaps: laps });
        if (usageData) setUsage(usageData);
      } catch (err: any) {
        setError(err.message || "Failed to generate coaching report.");
        // Still try to fetch usage for limit-reached errors
        api<CoachingUsage>(`/api/coaching/usage/me`).then(u => setUsage(u)).catch(() => {});
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [sessionId, stintIndex, session.trackId, session.carName, session.trackName]);

  const priorityItems = report?.priority_checklist && report.priority_checklist.length > 0
    ? report.priority_checklist
    : [];

  const detailSections = report?.sections?.filter((s) => classifySection(s.title) === "detail") || [];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.85)", zIndex: 1000,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "20px", overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg2)", width: "100%", maxWidth: "1200px",
          borderRadius: "12px", border: "1px solid var(--border)",
          padding: "24px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7)",
          margin: "20px 0",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid var(--border)", paddingBottom: "16px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <h2 style={{ fontSize: "20px", margin: 0 }}>Advanced Coaching Report — Run {stintIndex + 1}</h2>
              {usage && usage.full_reports_remaining != null && (
                <span style={{
                  fontSize: "11px", fontWeight: 600, padding: "3px 8px", borderRadius: "10px",
                  background: usage.full_reports_remaining > 0 ? "rgba(59,130,246,0.1)" : "rgba(239,68,68,0.1)",
                  color: usage.full_reports_remaining > 0 ? "var(--accent)" : "var(--bad)",
                }}>
                  {usage.full_reports_remaining} / {usage.full_report_cap} remaining
                </span>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text2)", marginTop: "4px" }}>{session.carName} @ {session.trackName}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "24px", padding: "4px 8px" }}>✕</button>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ marginBottom: "16px" }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" opacity="0.2" />
                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                </path>
              </svg>
            </div>
            <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--text)" }}>Analyzing your telemetry...</div>
            <div style={{ fontSize: "13px", color: "var(--text2)", marginTop: "8px" }}>Generating coaching report (10-15s)...</div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            {error.includes("free coaching reports") ? (
              <>
                <div style={{ fontSize: "40px", marginBottom: "16px" }}>&#128274;</div>
                <div style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Report Limit Reached</div>
                <div style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "20px" }}>
                  You've used all 3 free coaching reports this month. Upgrade to Pro for unlimited advanced reports.
                </div>
                <a href="/pricing" style={{
                  display: "inline-block", padding: "10px 24px", background: "var(--accent)",
                  color: "white", borderRadius: "6px", textDecoration: "none", fontWeight: 600, fontSize: "14px"
                }}>Upgrade to Pro</a>
              </>
            ) : (
              <div style={{ fontSize: "14px", color: "var(--bad)" }}>{error}</div>
            )}
          </div>
        )}

        {/* Report Content */}
        {report && !loading && !error && (
          <div className="coaching" style={{ maxWidth: "none" }}>
            {/* Scorecard & Summary */}
            <div style={{ display: "grid", gridTemplateColumns: report.score !== undefined ? "280px 1fr" : "1fr", gap: "24px", marginBottom: "24px" }}>
              {report.score !== undefined && (
                <div className="coaching__scorecard">
                  <div className="coaching__score-main">
                    <div className="coaching__score-circle" style={{ borderColor: report.score > 80 ? "var(--good)" : report.score > 60 ? "var(--warn)" : "var(--bad)" }}>
                      <div className="coaching__score-value" style={{ color: report.score > 80 ? "var(--good)" : report.score > 60 ? "var(--warn)" : "var(--bad)" }}>{report.score}</div>
                      <div className="coaching__score-label">Sense Score</div>
                    </div>
                  </div>
                  {report.technique_grades && Object.keys(report.technique_grades).length > 0 && (
                    <div className="coaching__score-grades">
                      {Object.entries(report.technique_grades).map(([label, grade]) => (
                        <div key={label} className="coaching__grade-item">
                          <div className="coaching__grade-label">{label}</div>
                          <div className={`coaching__grade-badge coaching__grade-badge--${getGradeColor(grade)}`}>{grade}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {report.pace_delta_seconds !== undefined && (
                    <div className="coaching__score-delta">
                      <div className="coaching__delta-value">+{report.pace_delta_seconds.toFixed(3)}s</div>
                      <div className="coaching__delta-label">vs Benchmark</div>
                    </div>
                  )}
                </div>
              )}
              {report.summary && (
                <div className="coaching__summary">
                  <div className="coaching__summary-label">Executive Overview</div>
                  <p className="coaching__summary-text">{report.summary}</p>
                  {report.car_balance_status && (
                    <div style={{ marginTop: "12px", fontSize: "12px", color: "var(--text2)", display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>Detected Balance:</span>
                      <span style={{ padding: "2px 6px", borderRadius: "4px", background: "rgba(255,255,255,0.05)", color: getSeverityColor(report.car_balance_status), textTransform: "uppercase", fontWeight: 700, fontSize: "10px", letterSpacing: "0.5px" }}>{report.car_balance_status}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Two-column layout */}
            <div className="coaching__grid">
              {/* Main content */}
              <div className="coaching__main">
                {/* Priority Cards */}
                {priorityItems.length > 0 && (
                  <div className="coaching__priorities">
                    <h3 className="coaching__section-title">🎯 Top Priorities</h3>
                    <div className="coaching__priority-list">
                      {priorityItems.map((item, i) => (
                        <div key={i} className="coaching__priority-card" style={{ borderLeftColor: getSeverityColor(item) }}>
                          <div className="coaching__priority-number">{i + 1}</div>
                          <div className="coaching__priority-text"><Markdown>{item}</Markdown></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detail Sections */}
                {detailSections.length > 0 && (
                  <div className="coaching__details">
                    {detailSections.map((section, idx) => {
                      const isExpanded = expandedSections.has(idx);
                      return (
                        <div key={idx} className={`coaching__detail-card ${isExpanded ? "coaching__detail-card--expanded" : ""}`}>
                          <button className="coaching__detail-header" onClick={() => toggleSection(idx)}>
                            <div className="coaching__detail-header-left">
                              <span className="coaching__detail-icon">{section.icon}</span>
                              <span className="coaching__detail-title">{section.title}</span>
                            </div>
                            <svg className={`coaching__chevron ${isExpanded ? "coaching__chevron--open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="coaching__detail-body coaching-content">
                              <Markdown>{section.content}</Markdown>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Action Plan */}
                {priorityItems.length > 0 && (
                  <div className="coaching__actions">
                    <h3 className="coaching__section-title">✅ Next Session To-Do</h3>
                    <div className="coaching__action-list">
                      {priorityItems.map((item, i) => (
                        <label key={i} className="coaching__action-item">
                          <input type="checkbox" className="coaching__checkbox" />
                          <span className="coaching__action-text"><Markdown>{item}</Markdown></span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="coaching__sidebar">
                {/* Corner Stoplights */}
                {report.corner_stoplights && Object.keys(report.corner_stoplights).length > 0 && (
                  <div className="coaching__corner-grid">
                    <h3 className="coaching__sidebar-title">Corner Entry / Apex / Exit</h3>
                    <div className="coaching__corners">
                      {Object.entries(report.corner_stoplights).map(([corner, stoplights]) => (
                        <div key={corner} className="coaching__corner-row">
                          <div className="coaching__corner-name">{corner}</div>
                          <div className="coaching__corner-stoplights">
                            <div style={{ display: "flex", gap: "6px" }}>
                              <span className={`coaching__corner-dot coaching__corner-dot--${stoplights.entry}`} title="Entry" />
                              <span className={`coaching__corner-dot coaching__corner-dot--${stoplights.apex}`} title="Apex" />
                              <span className={`coaching__corner-dot coaching__corner-dot--${stoplights.exit}`} title="Exit" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Community Best Laps */}
                {report.referenceLaps && report.referenceLaps.length > 0 && (() => {
                  const bestTime = Math.min(...report.referenceLaps!.map((l: any) => l.lap_time));
                  return (
                    <div className="coaching__reference-laps">
                      <h3 className="coaching__sidebar-title">Community Best Laps</h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {report.referenceLaps!.map((lap: any, i: number) => {
                          const delta = lap.lap_time - bestTime;
                          return (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "8px 10px", background: lap.is_self ? "rgba(59, 130, 246, 0.1)" : "rgba(255,255,255,0.02)", borderRadius: "6px", border: "1px solid var(--border)" }}>
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontWeight: 600, color: lap.is_self ? "var(--accent)" : "var(--text1)" }}>
                                  {lap.driver} {lap.is_self && "(You)"}
                                </span>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontWeight: 700 }}>{formatLapTime(lap.lap_time)}</div>
                                {delta > 0 && <div style={{ fontSize: "10px", color: "var(--text3)" }}>+{delta.toFixed(3)}s</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
